import { useEffect, useState } from 'react';
import { TaskTabBar } from './TaskTabBar';

interface Tab {
    key: string;
    label: React.ReactNode;
    content: React.ReactNode;
    trailing?: boolean;
}

interface TaskSidebarPanelProps {
    tabs: Tab[];
    initialKey?: string;
    /** Pair with `onActiveKeyChange` to control the active tab from the parent. */
    activeKey?: string;
    onActiveKeyChange?: (key: string) => void;
    /** Keep every visited tab mounted (hidden when inactive) so in-progress edits survive a switch. */
    keepMounted?: boolean;
    /** Drop the card chrome so the panel fills its host card edge to edge. */
    flush?: boolean;
    /** Shown under the tab bar whichever tab is active. */
    banner?: React.ReactNode;
}

// Flex column so tab content that sizes itself with flex-1 (e.g. FilesTabContent's preview
// mode) fills the panel instead of collapsing to auto height.
const CONTENT_CLASS = 'flex-1 min-h-0 flex flex-col';
const CARD_FRAME_CLASS =
    'h-full bg-white rounded-xl border border-gray-200 flex flex-col overflow-hidden font-inter';
const FLUSH_FRAME_CLASS = 'flex-1 min-h-0 flex flex-col overflow-hidden font-inter';

// Tabs visited so far, plus the active one before the effect records it.
function useVisitedKeys(activeKey: string, enabled: boolean): ReadonlySet<string> {
    const [visited, setVisited] = useState<ReadonlySet<string>>(() => new Set([activeKey]));
    useEffect(() => {
        if (!enabled) return;
        setVisited((prev) => (prev.has(activeKey) ? prev : new Set([...prev, activeKey])));
    }, [activeKey, enabled]);
    return visited.has(activeKey) ? visited : new Set([...visited, activeKey]);
}

export function TaskSidebarPanel({
    tabs,
    initialKey,
    activeKey: controlledActiveKey,
    onActiveKeyChange,
    keepMounted = false,
    flush = false,
    banner
}: TaskSidebarPanelProps) {
    const [uncontrolledActiveKey, setUncontrolledActiveKey] = useState(
        initialKey ?? tabs[0]?.key ?? ''
    );
    const activeKey = controlledActiveKey ?? uncontrolledActiveKey;
    const activeTab = tabs.find((t) => t.key === activeKey) ?? tabs[0];
    const visited = useVisitedKeys(activeTab?.key ?? '', keepMounted);

    const selectTab = (key: string) => {
        if (onActiveKeyChange) {
            onActiveKeyChange(key);
        } else {
            setUncontrolledActiveKey(key);
        }
    };

    return (
        <div className={flush ? FLUSH_FRAME_CLASS : CARD_FRAME_CLASS}>
            {tabs.length > 1 && (
                <TaskTabBar tabs={tabs} activeKey={activeTab?.key ?? ''} onSelect={selectTab} />
            )}
            {banner}
            {keepMounted ? (
                tabs
                    .filter((t) => visited.has(t.key))
                    .map((t) => (
                        <div
                            key={t.key}
                            className={t.key === activeTab?.key ? CONTENT_CLASS : 'hidden'}
                        >
                            {t.content}
                        </div>
                    ))
            ) : (
                <div className={CONTENT_CLASS}>{activeTab?.content}</div>
            )}
        </div>
    );
}
